import { ACKNOWLEDGEMENT_LOOKBACK_MS, isAcknowledged } from "@storyboard/dispatch-view/acknowledgement";
import type { OpenTask } from "@storyboard/dispatch-view/task";
import { formatChipTime } from "@/components/feed/tmsEvent/TmsEventChip";
import { tmsEventChipText } from "@/components/feed/tmsEvent/tmsEventDisplay";
import {
  firstAliasForFeed,
  isPhotoFile,
  membershipHasReadOrWrite,
  namedFileForContent,
  transcriptionForContent,
} from "@/store/feedSelectors";
import { store } from "@/store/store";
import type { Item, TmsEventRow } from "@/store/types";
import { PHOTO_MESSAGE_TEXT } from "@/types/driver";
import {
  type AcknowledgementRequirement,
  acknowledgeablesOn,
  DEFAULT_REQUIREMENT,
  feedWatermark,
  openTaskOn,
  ownAcknowledgements,
  tmsEventArrivedAt,
} from "./acknowledgements";
import {
  isTmsEventItemId,
  NO_TASKS_ELSEWHERE,
  OTHER_BOARD_TASK_ITEM_PREFIX,
  type TaskFold,
  type TasksElsewhere,
  TMS_EVENT_ITEM_PREFIX,
  taskItemIdOf,
} from "./tasks";

/**
 * One thing on a driver's channel that the view asks its team to acknowledge, as a Dispatch item row carries it: one
 * of the driver's messages, one of the driver's TMS events, or a task about the driver pinned to another board.
 */
export type DispatchItem = {
  /**
   * The item's id for a message; `tmsEvent:<anchor id>` for an event and `otherBoardTask:<task id>` for a task, so
   * none collide as row keys.
   */
  id: string;
  /**
   * What the row is about: a message, a TMS event, whose acknowledgement is keyed to its anchor, or a task pinned to
   * another board, whose acknowledgement is the task's own.
   */
  source: "message" | "tmsEvent" | "task";
  /** The driver's alias feed: the message's own, or the channel an event is shown on. */
  feedId: string;
  /** The driver whose channel the item is on. */
  driverMembershipId: string;
  /** When the item reached the server: the order rows sort in, and what a watermark covers. */
  createdAt: string;
  /** Whether anyone has read it: the receipt, painted as the row tint. An event is never unread. */
  unread: boolean;
  /** Whether a dispatcher has handled it, painted as the red dot. */
  acknowledged: boolean;
  /**
   * The transcription, {@link PHOTO_MESSAGE_TEXT}, or file name for a message; the chip's text for an event; the note
   * for a task, else {@link TASK_ITEM_TEXT}.
   */
  text: string | null;
  /** Whether `text` describes the driver's photo rather than quoting the driver. False on an event. */
  photo: boolean;
  /** The event behind a `tmsEvent` item; absent on a message. */
  tmsEvent?: TmsEventRow;
  /**
   * The open task about the item, which keeps it handled while open; on a `task` item, the task itself. Absent when
   * none names it.
   */
  task?: OpenTask;
};

/** What a message row shows, and whether it describes a photo rather than quoting the driver. */
export type MessageText = Pick<DispatchItem, "text" | "photo">;

/** The collections {@link computeDispatchItems} reads, for `useCollectionsCompute`. */
export const dispatchItemCollections = [
  "feed",
  "permission",
  "item",
  "transcription",
  "file",
  "workspaceCommandAlias",
  "feedAcknowledgement",
  "feedItemAcknowledgement",
  "anchorAcknowledgement",
  "tmsEvent",
  "task",
  "taskPinBoard",
  "taskAcknowledgement",
] as const;

/** Maps one store item to a Dispatch item, given the text and acknowledgement already looked up. */
export function dispatchItemFrom(
  item: Item,
  driverMembershipId: string,
  { text, photo }: MessageText,
  acknowledged: boolean,
): DispatchItem {
  return {
    id: item.id,
    source: "message",
    feedId: item.feedId,
    driverMembershipId,
    createdAt: item.createdAt,
    unread: item.unread === true,
    acknowledged,
    text,
    photo,
  };
}

/** Maps one TMS event on a driver's channel to a Dispatch item. */
export function dispatchItemFromTmsEvent(
  event: TmsEventRow,
  feedId: string,
  driverMembershipId: string,
  acknowledged: boolean,
): DispatchItem {
  return {
    id: `${TMS_EVENT_ITEM_PREFIX}${event.id}`,
    source: "tmsEvent",
    feedId,
    driverMembershipId,
    createdAt: tmsEventArrivedAt(event),
    unread: false,
    acknowledged,
    text: tmsEventChipText(event, formatChipTime),
    photo: false,
    tmsEvent: event,
  };
}

/** What a task item reads when its task has no note; the task line names what it is about. */
export const TASK_ITEM_TEXT = "Task";

/** Maps one task pinned to another board, on a driver's channel, to a Dispatch item that carries it as its task. */
export function dispatchItemFromOtherBoardTask(
  task: OpenTask,
  feedId: string,
  driverMembershipId: string,
  acknowledged: boolean,
): DispatchItem {
  return {
    id: `${OTHER_BOARD_TASK_ITEM_PREFIX}${task.task.id}`,
    source: "task",
    feedId,
    driverMembershipId,
    createdAt: task.task.createdAt,
    unread: false,
    acknowledged,
    text: task.task.note?.trim() || TASK_ITEM_TEXT,
    photo: false,
    task,
  };
}

/**
 * A message's text as the driver roster sources `recentMessage`: the transcription in the member's language, else
 * {@link PHOTO_MESSAGE_TEXT} for a photo, else the attached file's name.
 */
function messageText(item: Item, language: string): MessageText {
  const transcription = transcriptionForContent(item.contentId, language)?.transcriptionContent ?? null;
  if (transcription != null) return { text: transcription, photo: false };
  const file = namedFileForContent(item.contentId);
  if (isPhotoFile(file)) return { text: PHOTO_MESSAGE_TEXT, photo: true };
  return { text: file?.name ?? null, photo: false };
}

/**
 * Every item the view asks its team to acknowledge on an alias feed the member can read in the workspace: the driver's
 * non-deleted messages, with their text sourced the way the driver roster sources `recentMessage` (the transcription
 * in the member's language, else the attached file's name), the driver's TMS events of the kinds `requires`
 * takes, and the tasks about the driver pinned to other boards when it takes those.
 *
 * Only the channel's alias owner's messages are items: a dispatcher's reply or an automation's post is part of the
 * driver's thread, never a row of its own. With `teamAliases` only the feeds keyed to that team count, as the roster
 * narrows them. The window is the acknowledgement lookback: anything older is handled by age and never a row, so
 * loading a channel's history (opening its thread does) cannot grow the table. Each item's acknowledgement is folded
 * from its own log and its channel's watermark as of `now`; an item an open task in `tasks` names is acknowledged
 * while the task stays open, and carries it.
 */
export function computeDispatchItems({
  workspaceId,
  membershipId,
  language,
  teamAliases = [],
  requires = DEFAULT_REQUIREMENT,
  now = Date.now(),
  tasks,
  elsewhere = NO_TASKS_ELSEWHERE,
}: {
  workspaceId: string;
  membershipId: string;
  language: string;
  teamAliases?: readonly string[];
  requires?: AcknowledgementRequirement;
  now?: number;
  /** The workspace's open tasks, which keep the items they name handled; `NO_TASKS` while tasks are off. */
  tasks: TaskFold;
  /** The open tasks pinned to other boards, by driver; each is an item of its own when `requires` takes it. */
  elsewhere?: TasksElsewhere;
}): DispatchItem[] {
  const items: DispatchItem[] = [];
  const oldest = now - ACKNOWLEDGEMENT_LOOKBACK_MS;
  for (const feed of store.getAll("feed").values()) {
    if (feed.workspaceId !== workspaceId || !feed.isAliasFeed || !feed.aliasMembershipId) continue;
    if (!membershipHasReadOrWrite(feed.id, membershipId)) continue;
    if (teamAliases.length > 0) {
      const alias = firstAliasForFeed(feed.id);
      if (alias == null || !teamAliases.includes(alias.alias)) continue;
    }
    const watermark = feedWatermark(feed.id);
    for (const waiting of acknowledgeablesOn(feed, requires, elsewhere)) {
      if (Date.parse(waiting.arrivedAt) < oldest) continue;
      if (waiting.source === "otherBoardTask") {
        const own = ownAcknowledgements(waiting);
        const acknowledged = isAcknowledged({ arrivedAt: waiting.arrivedAt, own, watermark, now });
        items.push(dispatchItemFromOtherBoardTask(waiting.task, feed.id, feed.aliasMembershipId, acknowledged));
        continue;
      }
      // An open task on the item hands it to the task's board, which handles it whatever its own log says.
      const task = openTaskOn(waiting, tasks);
      const acknowledged =
        task !== null ||
        isAcknowledged({ arrivedAt: waiting.arrivedAt, own: ownAcknowledgements(waiting), watermark, now });
      const item =
        waiting.source === "driverMessage"
          ? dispatchItemFrom(waiting.item, feed.aliasMembershipId, messageText(waiting.item, language), acknowledged)
          : dispatchItemFromTmsEvent(waiting.event, feed.id, feed.aliasMembershipId, acknowledged);
      items.push(task === null ? item : { ...item, task });
    }
  }
  return items;
}

/**
 * The collections {@link computeTaskSubjectTexts} reads, for `useCollectionsCompute`. The task logs are not among them:
 * the compute reads the fold it is handed, whose change reruns it.
 */
export const taskSubjectTextCollections = ["item", "transcription", "file", "tmsEvent"] as const;

/**
 * What each open task's message or event reads, by the id a Dispatch item carries for it: a message's text as a
 * message row shows it, an event's chip text. A subject the store does not hold is left out, as is a task about a
 * driver.
 *
 * The board names an item task's subject on any row, and at the driver grain no item list is built, so this reads the
 * few subjects straight from the store.
 *
 * @param tasks - The workspace's open tasks.
 * @param language - The member's language, for a message's transcription.
 */
export function computeTaskSubjectTexts(tasks: TaskFold, language: string): ReadonlyMap<string, MessageText> {
  const texts = new Map<string, MessageText>();
  for (const open of tasks.open) {
    const itemId = taskItemIdOf(open);
    if (itemId === null || texts.has(itemId)) continue;
    const text = subjectTextOf(itemId, language);
    if (text) texts.set(itemId, text);
  }
  return texts;
}

/** One subject's text by its Dispatch item id; null when the store does not hold it. */
function subjectTextOf(itemId: string, language: string): MessageText | null {
  if (isTmsEventItemId(itemId)) {
    const event = store.get("tmsEvent", itemId.slice(TMS_EVENT_ITEM_PREFIX.length));
    return event ? { text: tmsEventChipText(event, formatChipTime), photo: false } : null;
  }
  const item = store.get("item", itemId);
  return item ? messageText(item, language) : null;
}
